// BudgetTracking.tsx

import {
  ChevronDown16Regular,
  ChevronRight16Regular,
} from "@fluentui/react-icons";
import { Input, Spinner, Tooltip, Tree, TreeItem, TreeItemLayout } from "@fluentui/react-components";
import React, { useState } from "react";
import { useGetMonthlyBudgetAllocation, useGetServiceGEMMapping } from "../../../hooks/useFinOpsBudget";
import { useParams, useSearchParams } from "react-router-dom";

import { BudgetLayout } from "../BudgetLayout";
import GemLayout from "./GemLayout";
import OverviewLayout from "./OverviewLayout";
import ServiceLayout from "./ServiceLayout";
import { Stack } from "@fluentui/react";
import { getBudgetFiscalYear } from "../../../utils/BudgetUtils";
import styles from "./BudgetTracking.less";
import { useNavigate } from "react-router-dom";

const BudgetTracking: React.FC = () => {
    const CURRENT_BUDGET_FISCAL_YEAR = React.useMemo(() => getBudgetFiscalYear(), []);
    const { data: serviceBudgetData = [], isLoading, error } = useGetMonthlyBudgetAllocation({ fiscalYear: CURRENT_BUDGET_FISCAL_YEAR });
    const { data: serviceGemMappingData = [], isLoading: isLoadingGemMapping, error: gemMappingError } = useGetServiceGEMMapping({ fiscalYear: CURRENT_BUDGET_FISCAL_YEAR });
    const [selected, setSelected] = useState<string>("overview");
    const [openItems, setOpenItems] = useState<string[]>([]);
    const [navCollapsed, setNavCollapsed] = React.useState(false);
    const navigate = useNavigate();
    const { gem: urlGem, service: urlService } = useParams<{ gem?: string; service?: string }>();
    const [searchParams] = useSearchParams();
    const queryGem = searchParams.get("gem");

    function useDebounce<T>(value: T, delay: number): T {
        const [debouncedValue, setDebouncedValue] = useState(value);

        React.useEffect(() => {
            const handler = setTimeout(() => {
            setDebouncedValue(value);
            }, delay);

            return () => clearTimeout(handler);
        }, [value, delay]);

        return debouncedValue;
}

    React.useEffect(() => {
    if (urlService) {
        setSelected(`service:${urlService}`);
        if (queryGem) {
        setOpenItems((prev) =>
            prev.includes(queryGem)
            ? prev
            : [...prev, queryGem]
        );
        }
    } else if (urlGem) {
        setSelected(`gem:${urlGem}`);
        setOpenItems((prev) =>
        prev.includes(urlGem)
            ? prev
            : [...prev, urlGem]
        );
    } else {
        setSelected("overview");
    }
    }, [urlService, urlGem, queryGem]);

    const serviceGemMapping: Record<string, string> = React.useMemo(() => {
        const mapping: Record<string, string> = {};
        serviceGemMappingData.forEach((item) => {
            if (item.serviceId && item.gemFullName) {
            mapping[item.serviceId] = item.gemFullName;
            }
    });
    return mapping;
    }, [serviceGemMappingData]);

    const combinedData = serviceBudgetData
    .filter(
        (item) =>
        item.serviceId &&
        serviceGemMapping[item.serviceId] &&
        item.serviceName &&
        item.serviceName.trim() !== ""
    )
    .map((item) => ({
        ...item,
        gem: item.serviceId ? serviceGemMapping[item.serviceId] : "Unknown",
    }));

    const gems = Array.from(new Set(combinedData.map((d) => d.gem)));
    const gemToServices: Record<string, string[]> = {};
    combinedData.forEach((item) => {
        if (!gemToServices[item.gem]) gemToServices[item.gem] = [];
        if (!gemToServices[item.gem].includes(item.serviceId ?? "Unknown")) {
        gemToServices[item.gem].push(item.serviceId ?? "Unknown");
        }
    });

    const [treeSearchText, setTreeSearchText] = useState("");
    const debouncedTreeSearchText = useDebounce(treeSearchText, 300);

    const handleOverviewClick = () => {
        setSelected("overview");
    };

    const handleGemClick = (gem: string) => {
        setSelected(`gem:${gem}`);
        setOpenItems((prev) =>
        prev.includes(gem)
            ? prev.filter((item) => item !== gem)
            : [...prev, gem]
        );
    };

    const handleServiceClick = (serviceId: string, gemName: string) => {
        setSelected(`service:${serviceId}`);
    };

    const serviceNameMap: Record<string, string> = React.useMemo(() => {
        const map: Record<string, string> = {};
        combinedData.forEach(item => {
            if (item.serviceId && item.serviceName) {
            map[item.serviceId] = item.serviceName;
            }
        });
        return map;
    }, [combinedData]);

    const filteredGems = React.useMemo(() => {
        if (!debouncedTreeSearchText.trim()) return gems;

        const lowerText = debouncedTreeSearchText.toLowerCase();

        const matchedGems = gems.filter((gem) =>
            gem.toLowerCase().includes(lowerText)
        );

        const matchedByService = gems.filter((gem) =>
            (gemToServices[gem] ?? []).some((sid) =>
            (serviceNameMap[sid] ?? "").toLowerCase().includes(lowerText)
            )
        );

        return Array.from(new Set([...matchedGems, ...matchedByService]));
    }, [debouncedTreeSearchText, gems, gemToServices, serviceNameMap]);

    const renderTree = React.useMemo(() => {
        return (            
            <Tree aria-label="GEM Tree">
                <TreeItem itemType="leaf" value="overview">
                <TreeItemLayout
                    onClick={() => handleOverviewClick()}
                    className={`${styles.treeItem} ${selected === "overview" ? styles.selected : ""}`}
                >
                    Overview
                </TreeItemLayout>
                </TreeItem>

                <Input
                    className={styles.treeSearchInput}
                    type="text"
                    placeholder="Search GEM or Service..."
                    value={treeSearchText}
                    onChange={(e) => setTreeSearchText(e.target.value)}
                />

                {filteredGems.sort().map((gem) => {
                    const isOpen = openItems.includes(gem);
                    const services = (gemToServices[gem] ?? []).filter((sid) =>
                        !debouncedTreeSearchText ||
                        (serviceNameMap[sid] ?? "").toLowerCase().includes(debouncedTreeSearchText.toLowerCase())
                    );
                    return (

                        <TreeItem itemType="branch" key={gem} value={gem}>
                        <TreeItemLayout
                            onClick={() => handleGemClick(gem)}
                            className={`${styles.treeItem} ${selected === `gem:${gem}` ? styles.selected : ""}`}
                            iconBefore={isOpen ? <ChevronDown16Regular /> : <ChevronRight16Regular />}
                            expandIcon={null}
                        >
                            {gem}
                        </TreeItemLayout>
                        {isOpen && (
                        <div className={styles.subTreeContainer}>
                            {services.map((service) => (
                            <TreeItem key={service} itemType="leaf" value={service}>
                                <Tooltip content={serviceNameMap[service]} relationship="label">
                                    <TreeItemLayout
                                    onClick={() => handleServiceClick(service, gem)}
                                    className={`${styles.treeItem} ${selected === `service:${service}` ? styles.selected : ""}`}
                                    style={{ paddingLeft: 24 }}
                                    title={serviceNameMap[service]}
                                    >
                                    {serviceNameMap[service] ?? service}
                                    </TreeItemLayout>
                                </Tooltip>
                            </TreeItem>
                            ))}
                        </div>
                        )}
                        </TreeItem>
                    );
                    })}
            </Tree>
        );
    }, [filteredGems, openItems, selected, debouncedTreeSearchText, serviceNameMap, gemToServices, treeSearchText]);

    if (isLoading) return <Spinner label="Loading budget data..." />;
    if (error) return <div>Error loading data: {error.message}</div>;

    return (
        <BudgetLayout noPadding={true} onNavCollapseChange={setNavCollapsed}>
            <Stack horizontal className={styles.page}>
                <Stack.Item className={styles.sidebar}>{renderTree}</Stack.Item>
                <Stack.Item grow className={styles.contentWrapper}>
                        {selected.startsWith("overview") && 
                            <OverviewLayout 
                                data={combinedData}
                                isLoadingBudgetData={isLoading}
                                currentFiscalYear={CURRENT_BUDGET_FISCAL_YEAR}
                        />}
                        {selected.startsWith("gem:") && (
                            <GemLayout
                                gem={selected.split(":")[1]}
                                data={combinedData.filter((d) => d.gem === selected.split(":")[1])}
                                isLoadingBudgetData={isLoading}
                                currentFiscalYear={CURRENT_BUDGET_FISCAL_YEAR}
                            />
                        )}
                        {selected.startsWith("service:") && (
                            <ServiceLayout
                                service={combinedData.find((d) => d.serviceId === selected.split(":")[1])?.serviceName ?? "Unknown"}
                                data={combinedData.filter((d) => d.serviceId === selected.split(":")[1])}
                                isLoadingBudgetData={isLoading}
                                currentFiscalYear={CURRENT_BUDGET_FISCAL_YEAR}
                            />
                        )}
                </Stack.Item>
            </Stack>
        </BudgetLayout>
    );
};

export default BudgetTracking;
